import React, { useState } from 'react'
import Text from 'components/_UI/Text'
import { Column } from 'styled/alignment/Column'
import colors from 'theme/colors'
import Loader from 'components/_UI/Loader'
import Fieldset from 'components/_UI/Fieldset'
import { FullyCentered } from 'styled/alignment/Center'
import styled, { keyframes, css } from 'styled-components'
import { useForm } from 'react-hook-form'
import fieldValidations from 'helpers/fieldValidations'
import { User, ArrowRight, AlertCircle } from 'lucide-react'

const errorMessages = {
    save: 'Ocorreu um erro ao salvar seus dados, tente novamente.'
}

export default function CompleteNameStep({ onComplete, hideBack }) {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: { firstName: '', lastName: '' },
    })

    const [isLoading, setIsLoading] = useState(false)
    const [saveError, setSaveError] = useState(false)

    async function onSubmit({ firstName, lastName }) {
        setSaveError(false)
        setIsLoading(true)
        try {
            await onComplete({ first_name: firstName, last_name: lastName })
        } catch (error) {
            setSaveError(true)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Wrapper isModal={hideBack}>
            {hideBack ? (
                <ModalHeader>
                    <HeaderIconRing>
                        <User size={18} color={colors.occasionalPurple} />
                    </HeaderIconRing>
                    <ModalTitle>Como podemos te chamar?</ModalTitle>
                    <ModalSubtitle>Complete seu nome para finalizar o cadastro</ModalSubtitle>
                </ModalHeader>
            ) : (
                <FullPageHeader>
                    <Text color={colors.nightDark} type='bold' name='highlight'>
                        Para finalizar, complete
                    </Text>
                    <Text color={colors.nightDark} type='bold' name='highlight'>
                        seu nome e sobrenome.
                    </Text>
                </FullPageHeader>
            )}

            <FormWrapper onSubmit={handleSubmit(onSubmit)} isModal={hideBack}>
                <FieldGroup>
                    <FieldLabel>
                        Nome<Required>*</Required>
                    </FieldLabel>
                    <Fieldset
                        layout="squired"
                        placeholder="Seu nome"
                        name="firstName"
                        register={register}
                        validations={fieldValidations.name()}
                    />
                    {errors.firstName?.message && (
                        <ErrorText>{errors.firstName.message}</ErrorText>
                    )}
                </FieldGroup>

                <FieldGroup>
                    <FieldLabel>
                        Sobrenome<Required>*</Required>
                    </FieldLabel>
                    <Fieldset
                        layout="squired"
                        placeholder="Seu sobrenome"
                        name="lastName"
                        register={register}
                        validations={fieldValidations.name('sobrenome')}
                    />
                    {errors.lastName?.message && (
                        <ErrorText>{errors.lastName.message}</ErrorText>
                    )}
                </FieldGroup>

                {isLoading ? (
                    <FullyCentered height='52px'>
                        <Loader />
                    </FullyCentered>
                ) : (
                    <ConfirmButton type="submit">
                        <span>Continuar</span>
                        <ArrowRight size={16} />
                    </ConfirmButton>
                )}

                {saveError && (
                    <ErrorBanner>
                        <AlertCircle size={15} color={colors.danger} />
                        <ErrorMsg>{errorMessages.save}</ErrorMsg>
                    </ErrorBanner>
                )}
            </FormWrapper>
        </Wrapper>
    )
}

// ── Animations ──────────────────────────────────────────────────────────────

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

// ── Styled components ────────────────────────────────────────────────────────

const Wrapper = styled(Column)`
    width: 100%;
    animation: ${fadeUp} 0.3s ease-out both;

    ${({ isModal }) => isModal && css`
        padding: 24px 28px 28px;
    `}
`;

const ModalHeader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 20px;
`;

const HeaderIconRing = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: ${colors.occasionalPurple}12;
    border: 1.5px solid ${colors.occasionalPurple}28;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
`;

const ModalTitle = styled.h3`
    font-size: 17px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 4px 0;
    line-height: 1.3;
`;

const ModalSubtitle = styled.p`
    font-size: 13px;
    color: ${colors.night};
    margin: 0;
    line-height: 1.4;
`;

const FullPageHeader = styled(Column)`
    margin-bottom: 16px;
`;

const FormWrapper = styled.form`
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;

    ${({ isModal }) => isModal && css`
        border-top: 1px solid ${colors.neutralDark};
        padding-top: 16px;
    `}
`;

const FieldGroup = styled.div`
    width: 100%;
`;

const FieldLabel = styled.label`
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightDark};
    letter-spacing: 0.01em;
    margin-bottom: 6px;
`;

const Required = styled.span`
    color: ${colors.danger};
`;

const ErrorText = styled.p`
    color: ${colors.danger};
    font-size: 12px;
    margin-top: 4px;
`;

const ConfirmButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 13px 24px;
    border-radius: 50px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};
    box-shadow: 0 4px 14px ${colors.occasionalPurple}38;
    transition: opacity 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
    margin-top: 4px;

    &:hover { opacity: 0.9; box-shadow: 0 6px 18px ${colors.occasionalPurple}48; }
    &:active { transform: scale(0.98); }
`;

const ErrorBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: #FEF2F2;
    border: 1px solid ${colors.dangerLight};
    border-radius: 8px;
    width: 100%;
`;

const ErrorMsg = styled.span`
    font-size: 13px;
    color: ${colors.danger};
`;
